import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { Facility } from "types/facility.type";
import {
  createSeparator,
  patchSeparator,
  SeparatorQueryKey,
  TSeparatorMetadataSchema,
  separatorMetadataSchema,
} from "api/separator.api";
import { FacilityQueryKey } from "api/facility.api";
import { Separator, SeparatorType } from "types/separator/separator.type";

export const useCreateOrUpdateSeparatorLogic = (
  facility?: Facility,
  onSuccess?: (s: Separator) => void,
  separatorToUpdate?: Separator,
) => {
  const queryClient = useQueryClient();

  const [isModalOpen, setIsModalOpen] = useState(false);

  const form = useForm<TSeparatorMetadataSchema>({
    resolver: zodResolver(separatorMetadataSchema),
    defaultValues: {
      type: SeparatorType.OilAndSlam,
      avtalFemarskontroll: false,
      avtalHalvarsKontroll: false,
      avtalSlamtomning: false,
      tomningReminder: {
        january: false,
        february: false,
        march: false,
        april: false,
        may: false,
        june: false,
        july: false,
        august: false,
        september: false,
        october: false,
        november: false,
        december: false,
      },
    },
  });

  const resetForm = useCallback(() => {
    separatorToUpdate
      ? form.reset({
          type: separatorToUpdate.type,
          identifier: separatorToUpdate.identifier,
          fritextType: separatorToUpdate.fritextType,
          notes: separatorToUpdate.notes,
          coordinates: separatorToUpdate.coordinates,
          propertyDesignation: separatorToUpdate.propertyDesignation,
          avtalFemarskontroll: separatorToUpdate.avtalFemarskontroll || false,
          avtalHalvarsKontroll: separatorToUpdate.avtalHalvarsKontroll || false,
          avtalSlamtomning: separatorToUpdate.avtalSlamtomning || false,
          tomningReminder: separatorToUpdate.tomningReminder || {
            january: false,
            february: false,
            march: false,
            april: false,
            may: false,
            june: false,
            july: false,
            august: false,
            september: false,
            october: false,
            november: false,
            december: false,
          },
        })
      : form.reset();
  }, [form, separatorToUpdate]);

  useEffect(() => {
    if (separatorToUpdate) {
      resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [separatorToUpdate]);

  const onModalClose = () => {
    setIsModalOpen(false);
  };

  const afterModalClose = () => {
    resetForm();
  };

  // CREATE

  const createMutation = useMutation({
    ...createSeparator(),
    onSuccess: (s: Separator) => {
      if (onSuccess) {
        onSuccess(s);
      } else {
        queryClient.invalidateQueries({
          queryKey: [FacilityQueryKey.GetOne],
        });
      }
      onModalClose();
    },
  });

  // PATCH
  const patchMutation = useMutation({
    ...patchSeparator(),
    onSuccess: (s: Separator) => {
      if (onSuccess) {
        onSuccess(s);
      } else {
        queryClient.invalidateQueries({
          queryKey: [SeparatorQueryKey.GetOne],
        });
      }
      onModalClose();
    },
  });

  const onSubmit = form.handleSubmit((data: TSeparatorMetadataSchema) => {
    if (separatorToUpdate) {
      //PATCH
      patchMutation.mutate({
        separatorId: separatorToUpdate.id,
        payload: data,
      });
    } else {
      // CREATE
      if (!facility) {
        throw Error("facility is missing when creating separator");
      }
      createMutation.mutate({ facilityUuid: facility.uuid, payload: data });
    }
  });

  const selectedSeparatorType = form.watch("type");

  return {
    isModalOpen,
    openModal: () => setIsModalOpen(true),
    onModalClose,
    form,
    onSubmit,
    selectedSeparatorType,
    onNewSeparatorModalClose: onModalClose,
    afterClose: afterModalClose,
    isPending: createMutation.isPending || patchMutation.isPending,
  };
};

export type AddSeparatorLogicRetVal = ReturnType<
  typeof useCreateOrUpdateSeparatorLogic
>;
